// Frontend API client. Calls our serverless backend (which proxies Claude).
// Includes retries with backoff for transient failures.

const MAX_RETRIES = 2;
const BASE_BACKOFF_MS = 800;

async function apiCall(endpoint, body, { signal } = {}) {
  let lastError;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    if (signal?.aborted) throw new DOMException("Aborted", "AbortError");
    try {
      const res = await fetch(`https://sp500-scanner-api.mscanner.workers.dev/api/${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        signal,
      });

      // Retry on rate limit or server error
      if (res.status === 429 || res.status >= 500) {
        const retryAfter = parseInt(res.headers.get("retry-after") || "0", 10);
        const delay = retryAfter > 0
          ? retryAfter * 1000
          : BASE_BACKOFF_MS * Math.pow(2, attempt);
        lastError = new Error(`HTTP ${res.status}`);
        if (attempt < MAX_RETRIES) {
          await sleep(delay, signal);
          continue;
        }
      }

      const data = await res.json();
      if (!res.ok || data.error) {
        const msg = data.error?.message || `HTTP ${res.status}`;
        const err = new Error(msg);
        err.status = res.status;
        throw err;
      }
      return data;
    } catch (err) {
      if (err.name === "AbortError") throw err;
      lastError = err;
      if (attempt < MAX_RETRIES && !err.status) {
        // Network error — retry
        await sleep(BASE_BACKOFF_MS * Math.pow(2, attempt), signal);
        continue;
      }
      throw err;
    }
  }
  throw lastError || new Error("API call failed");
}

async function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException("Aborted", "AbortError"));
    const t = setTimeout(resolve, ms);
    signal?.addEventListener("abort", () => {
      clearTimeout(t);
      reject(new DOMException("Aborted", "AbortError"));
    }, { once: true });
  });
}

/**
 * Pre-screen: find top momentum candidates across S&P 500.
 * @param {string} sector - GICS sector name or "ALL"
 * @param {number} count - Number of candidates to return (10-50)
 * @param {AbortSignal} signal
 */
export async function prescreen({ sector = "ALL", count = 50, customTickers, signal }) {
  const body = { sector, count };
  if (Array.isArray(customTickers) && customTickers.length > 0) {
    body.customTickers = customTickers;
  }
  const data = await apiCall("prescreen", body, { signal });
  return data.candidates || [];
}

/**
 * Full analysis: combined options chain + AI verdict for one ticker.
 * @param {string} ticker
 * @param {Object} marketData - Market data from prescreen
 * @param {AbortSignal} signal
 */
export async function analyze({ ticker, marketData, signal }) {
  const data = await apiCall("analyze", { ticker, marketData }, { signal });
  return data.analysis || null;
}

/**
 * Health check.
 */
export async function healthCheck() {
  const res = await fetch("/api/health");
  if (!res.ok) throw new Error(`Health check failed: ${res.status}`);
  return res.json();
}

/**
 * Concurrent map with limited parallelism.
 * Parallelizes safely — multiple requests in flight at once, but bounded.
 */
export async function parallelMap(items, fn, { concurrency = 3, signal } = {}) {
  const results = new Array(items.length);
  let cursor = 0;

  async function worker() {
    while (cursor < items.length) {
      if (signal?.aborted) return;
      const i = cursor++;
      try {
        results[i] = await fn(items[i], i);
      } catch (err) {
        if (err.name === "AbortError") return;
        results[i] = { error: err.message };
      }
    }
  }

  const workers = Array.from(
    { length: Math.min(concurrency, items.length) },
    () => worker()
  );
  await Promise.all(workers);
  return results;
}
